import React from "react";
import { useNavigate } from "react-router-dom";
import {
  People as PeopleIcon,
  Dashboard as DashboardIcon,
  Folder as FolderIcon,
  Work as WorkIcon,
  Description as LetterIcon,
  Error as ErrorIcon,
  Group as GroupIcon,
  AccountBalance as AccountBalanceIcon,
  HomeWork as HomeWorkIcon,
  InsertChart as InsertChartIcon,
  Hub as HubIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import { useContext } from "react";
import LocalPoliceIcon from '@mui/icons-material/LocalPolice';
import { ThemeContext } from "../../utils/ThemeContext"; // adjust path if needed

const Sidebar = ({
  userFullName,
  openPropertiesMenu,
  setOpenPropertiesMenu,
  hasPermission,
  isActive,
  handleSignOut,
  collapsed,
  setCollapsed,
}) => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:768px)");
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const { theme } = useContext(ThemeContext);
  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile) setDrawerOpen(false); // auto-close drawer on mobile
  };

  const menuItems = [
    { label: "Home", icon: <DashboardIcon />, path: "/home", permission: "view_dashboard" },
    { label: "Users", icon: <PeopleIcon />, path: "/home/users", permission: "view_users" },
    { label: "Architectural Request", icon: <AccountBalanceIcon />, path: "/home/architecturalrequest", permission: "view_architectural_requests" },
    { label: "Violations", icon: <ErrorIcon />, path: "/home/violations", permission: "view_violations" },
    { label: "Work Orders", icon: <WorkIcon />, path: "/home/workorders", permission: "view_work_orders" },
    { label: "Gate Security", icon: <LocalPoliceIcon />, path: "/home/GateSecurity", permission: "view_architectural_requests" },
    { label: "File Manager", icon: <FolderIcon />, path: "/home/filemanager", permission: "view_file_manager" },
    { label: "Reports", icon: <InsertChartIcon />, path: "/home/reports", permission: "view_reports" },
    { label: "Integrations", icon: <HubIcon />, path: "/home/integrations", permission: "view_reports" },
    { label: "Owners Page", icon: <GroupIcon />, path: "/home/memberspage", permission: "access_owners_page" },
  ];

  const SidebarContent = (
    <Box
      sx={{
        width: collapsed ? "80px" : "250px",
        backgroundColor: theme.sidebarColor || "#2c3e50",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        color: "white",
        position: "fixed",
        left: 0,
        top: 0,
        zIndex: 1200,
      }}
    >
      {/* Scrollable Content */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          '&::-webkit-scrollbar': { width: '8px' },
          '&::-webkit-scrollbar-track': { backgroundColor: '#1c2b36' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#5c8599',
            borderRadius: '10px',
            border: '2px solid #2c3e50',
          },
          '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#76a4bc' },
          scrollbarWidth: 'thin',
          scrollbarColor: '#5c8599 #1c2b36',
        }}
      >
        {/* Collapse Toggle */}
        <Box sx={{ display: "flex", justifyContent: collapsed ? "center" : "flex-end", padding: "1rem" }}>
          <IconButton onClick={() => setCollapsed((prev) => !prev)} sx={{ color: "white" }}>
            {collapsed ? <ExpandMore /> : <ExpandLess />}
          </IconButton>
        </Box>

        {/* Logo */}
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", paddingBottom: "1rem" }}>
          <img
            src="/img/logo2.png"
            alt="MY HOA Logo"
            style={{ width: collapsed ? "40px" : "100px", height: "auto" }}
          />
        </Box>

        {/* Menu Items */}
        <Box>
          {menuItems.map(({ label, icon, path, permission }) =>
            hasPermission(permission) && (
              <Tooltip key={label} title={collapsed ? label : ""} placement="right">
                <button
                  onClick={() => handleNavigate(path)}
                  className={`nav-btn ${isActive(path) ? "active" : ""}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "10px 20px",
                    width: "100%",
                    background: "transparent",
                    border: "none",
                    color: "white",
                    cursor: "pointer",
                  }}
                >
                  {icon}
                  {!collapsed && <span style={{ marginLeft: "10px" }}>{label}</span>}
                </button>
              </Tooltip>
            )
          )}

          {/* Properties & Members */}
          {(hasPermission("view_properties") || hasPermission("view_members")) && (
            <>
              {collapsed ? (
                <>
                  {hasPermission("view_properties") && (
                    <Tooltip title="Properties" placement="right">
                      <button
                        onClick={() => handleNavigate("/home/properties")}
                        className={`nav-btn ${isActive("/home/properties") ? "active" : ""}`}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          padding: "10px 20px",
                          width: "100%",
                          background: "transparent",
                          border: "none",
                          color: "white",
                          cursor: "pointer",
                        }}
                      >
                        <HomeWorkIcon />
                      </button>
                    </Tooltip>
                  )}
                  {hasPermission("view_members") && (
                    <Tooltip title="Members" placement="right">
                      <button
                        onClick={() => handleNavigate("/home/clubmembers")}
                        className={`nav-btn ${isActive("/home/clubmembers") ? "active" : ""}`}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          padding: "10px 20px",
                          width: "100%",
                          background: "transparent",
                          border: "none",
                          color: "white",
                          cursor: "pointer",
                        }}
                      >
                        <GroupIcon />
                      </button>
                    </Tooltip>
                  )}
                </>
              ) : (
                <>
                  <Tooltip title="Properties & Members" placement="right">
                    <button
                      onClick={() => setOpenPropertiesMenu(!openPropertiesMenu)}
                      className={`nav-btn ${isActive("/home/properties") || isActive("/home/clubmembers") ? "active" : ""}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "10px 20px",
                        width: "100%",
                        background: "transparent",
                        border: "none",
                        color: "white",
                        cursor: "pointer",
                      }}
                    >
                      <HomeWorkIcon />
                      <span style={{ marginLeft: "10px", flexGrow: 1 }}>Properties & Members</span>
                      {openPropertiesMenu ? <ExpandLess /> : <ExpandMore />}
                    </button>
                  </Tooltip>

                  {openPropertiesMenu && (
                    <Box sx={{ pl: 4 }}>
                      {hasPermission("view_properties") && (
                        <button
                          onClick={() => handleNavigate("/home/properties")}
                          className={`nav-btn sub-nav-btn ${isActive("/home/properties") ? "active" : ""}`}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            padding: "8px 0",
                            background: "transparent",
                            border: "none",
                            color: "white",
                            cursor: "pointer",
                          }}
                        >
                          <HomeWorkIcon sx={{ fontSize: 18, marginRight: "8px" }} />
                          <span>Properties</span>
                        </button>
                      )}
                      {hasPermission("view_members") && (
                        <button
                          onClick={() => handleNavigate("/home/clubmembers")}
                          className={`nav-btn sub-nav-btn ${isActive("/home/clubmembers") ? "active" : ""}`}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            padding: "8px 0",
                            background: "transparent",
                            border: "none",
                            color: "white",
                            cursor: "pointer",
                          }}
                        >
                          <GroupIcon sx={{ fontSize: 18, marginRight: "8px" }} />
                          <span>Members</span>
                        </button>
                      )}
                    </Box>
                  )}
                </>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Footer */}
      <Box sx={{ padding: "1rem", borderTop: "1px solid rgba(255,255,255,0.1)", textAlign: "center" }}>
        {!collapsed && <Typography variant="body2">{userFullName}</Typography>}
        <IconButton
          onClick={handleSignOut}
          sx={{
            color: "white",
            marginTop: "0.5rem",
            "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
          }}
        >
          <LogoutIcon />
          {!collapsed && (
            <Typography variant="body2" sx={{ marginLeft: "5px" }}>
              Sign Out
            </Typography>
          )}
        </IconButton>
      </Box>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          textAlign: "center",
          width: "100%",
        }}
      >
        © My HOA 2025
      </Typography>
    </Box>
  );

  return (
    <>
      {isMobile ? (
        <>
          <IconButton
            onClick={() => setDrawerOpen(true)}
            sx={{ position: "fixed", top: 10, left: 10, zIndex: 1300 }}
          >
            <MenuIcon />
          </IconButton>
          <Drawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            sx={{
              width: "250px", // for desktop
              maxWidth: "100%", // ensure it fits on smaller screens
              "& .MuiDrawer-paper": {
                width: "250px", // this will set the paper size for the sidebar
                maxWidth: "100%",
                backgroundColor: theme.sidebarColor || "#2c3e50",
                color: "white",
                height: "100vh",
              },
            }}
          >
            {SidebarContent}
          </Drawer>
        </>
      ) : (
        SidebarContent
      )}
    </>
  );
};

export default Sidebar;
